import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';

import {
  Close,
  InfoOutlined,
  KeyboardArrowDown
} from '@mui/icons-material';

import {
  dialogHeaderStyles,
  menuProps,
  textFieldSx,
  hideScrollbarSx
} from '../../../Common/components/Styles/InputStyles';

import CommonDialogActions from '../../../Common/components/CommonDialogActions';

import { inviteUserModalSx } from './InviteUserModalStyles';

const accountTypes = {
  MEDICAL_PROVIDER: 'medicalProvider',
  CLINICAL_STAFF: 'clinicalStaff'
};

const initialForm = {
  accountType: accountTypes.MEDICAL_PROVIDER,
  title: '',
  lastName: '',
  firstName: '',
  username: '',
  email: '',
  confirmEmail: '',
  isAdmin: ''
};

const InviteUserModal = ({ open, onClose, onSubmit }) => {
  const [form, setForm] = useState(initialForm);

  const handleChange = field => event => {
    setForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleClose = () => {
    setForm(initialForm);
    onClose();
  };

  const isValid =
    form.accountType &&
    form.title &&
    form.lastName.trim() &&
    form.firstName.trim() &&
    form.email.trim() &&
    form.confirmEmail.trim() &&
    form.isAdmin &&
    form.email.trim().toLowerCase() ===
      form.confirmEmail.trim().toLowerCase();

  const handleSubmit = () => {
    if (!isValid) return;

    if (typeof onSubmit === 'function') {
      onSubmit(form);
    }

    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: inviteUserModalSx.dialogPaper
      }}
    >
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>
            User
          </Typography>

          <Typography sx={dialogHeaderStyles.subtitle}>
            Invite a new user to the organization.
          </Typography>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={handleClose}
          size="small"
          sx={dialogHeaderStyles.dialogheaderclose}
        >
          <Close sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          ...inviteUserModalSx.dialogContent,
          ...hideScrollbarSx
        }}
      >
        <Box sx={inviteUserModalSx.accountSection}>
          <Typography sx={inviteUserModalSx.sectionTitle}>
            Choose Account Type
          </Typography>

          <RadioGroup
            row
            value={form.accountType}
            onChange={handleChange('accountType')}
            sx={inviteUserModalSx.accountTypeRadioGroup}
          >
            <FormControlLabel
              value={accountTypes.MEDICAL_PROVIDER}
              control={
                <Radio
                  size="small"
                  sx={inviteUserModalSx.accountTypeRadio}
                />
              }
              label={
                <Box sx={inviteUserModalSx.accountTypeLabelWrapper}>
                  <Typography sx={inviteUserModalSx.accountTypeLabel}>
                    Medical Provider
                  </Typography>

                  <Tooltip
                    arrow
                    placement="bottom"
                    title="Complete access to electronic health records and allowed to save and sign progress notes and clinical documents electronically."
                    slotProps={{
                      tooltip: {
                        sx: inviteUserModalSx.tooltip
                      },
                      arrow: {
                        sx: inviteUserModalSx.tooltipArrow
                      }
                    }}
                  >
                    <InfoOutlined
                      sx={inviteUserModalSx.medicalProviderInfoIcon}
                    />
                  </Tooltip>
                </Box>
              }
              sx={inviteUserModalSx.formControlLabel}
            />

            <FormControlLabel
              value={accountTypes.CLINICAL_STAFF}
              control={
                <Radio
                  size="small"
                  sx={inviteUserModalSx.accountTypeRadio}
                />
              }
              label={
                <Box sx={inviteUserModalSx.accountTypeLabelWrapper}>
                  <Typography sx={inviteUserModalSx.accountTypeLabel}>
                    Clinical Staff
                  </Typography>

                  <Tooltip
                    arrow
                    placement="bottom"
                    title="Access to Medical History Update, Diagnosis Update, and Patient Documents. Medical notes and clinical documents can be routed to providers for signatures. All medical notes and documents can be viewed, downloaded and printed for medical relevance."
                    slotProps={{
                      tooltip: {
                        sx: inviteUserModalSx.tooltip
                      },
                      arrow: {
                        sx: inviteUserModalSx.tooltipArrow
                      }
                    }}
                  >
                    <InfoOutlined
                      sx={inviteUserModalSx.clinicalStaffInfoIcon}
                    />
                  </Tooltip>
                </Box>
              }
              sx={inviteUserModalSx.formControlLabel}
            />
          </RadioGroup>

          <FormControl
            fullWidth
            size="small"
            sx={textFieldSx}
          >
            <InputLabel id="user-title-label">
              Title
            </InputLabel>

            <Select
              labelId="user-title-label"
              id="user-title"
              value={form.title}
              label="Title"
              onChange={handleChange('title')}
              IconComponent={KeyboardArrowDown}
              MenuProps={menuProps}
            >
              <MenuItem value="">Select Title</MenuItem>
              <MenuItem value="MD">MD</MenuItem>
              <MenuItem value="DO">DO</MenuItem>
              <MenuItem value="NP">NP</MenuItem>
              <MenuItem value="PA">PA</MenuItem>
              <MenuItem value="RN">RN</MenuItem>
              <MenuItem value="LPN">LPN</MenuItem>
              <MenuItem value="MA">MA</MenuItem>
            </Select>
          </FormControl>

          <Box sx={inviteUserModalSx.adminPrivilegeSection}>
            <Box sx={inviteUserModalSx.adminPrivilegeLabelWrapper}>
              <Typography
                component="span"
                sx={inviteUserModalSx.sectionTitle}
              >
                Admin Privilege

                <Box
                  component="span"
                  sx={inviteUserModalSx.requiredAsterisk}
                >
                  *
                </Box>
              </Typography>

              <Tooltip
                arrow
                placement="bottom"
                title="Privilege to add or delete users and patients in the system and edit organization profile."
                slotProps={{
                  tooltip: {
                    sx: inviteUserModalSx.tooltip
                  },
                  arrow: {
                    sx: inviteUserModalSx.tooltipArrow
                  }
                }}
              >
                <InfoOutlined
                  sx={inviteUserModalSx.adminInfoIcon}
                />
              </Tooltip>
            </Box>

            <RadioGroup
              row
              name="isAdmin"
              value={form.isAdmin}
              onChange={handleChange('isAdmin')}
              sx={inviteUserModalSx.adminRadioGroup}
            >
              <FormControlLabel
                value="yes"
                control={
                  <Radio
                    size="small"
                    sx={inviteUserModalSx.adminRadio}
                  />
                }
                label="Yes"
                sx={inviteUserModalSx.adminOptionLabel}
              />

              <FormControlLabel
                value="no"
                control={
                  <Radio
                    size="small"
                    sx={inviteUserModalSx.adminRadio}
                  />
                }
                label="No"
                sx={inviteUserModalSx.adminOptionLabel}
              />
            </RadioGroup>
          </Box>
        </Box>

        <Divider />

        <Box sx={inviteUserModalSx.userInformationSection}>
          <Typography sx={inviteUserModalSx.userInformationTitle}>
            User Information
          </Typography>

          <Stack spacing={1.9}>
            <FormControl
              fullWidth
              required
              sx={textFieldSx}
            >
              <InputLabel htmlFor="last-name">
                Last Name
              </InputLabel>

              <OutlinedInput
                id="last-name"
                label="Last Name"
                value={form.lastName}
                onChange={handleChange('lastName')}
              />
            </FormControl>

            <FormControl
              fullWidth
              required
              sx={textFieldSx}
            >
              <InputLabel htmlFor="first-name">
                First Name
              </InputLabel>

              <OutlinedInput
                id="first-name"
                label="First Name"
                value={form.firstName}
                onChange={handleChange('firstName')}
              />
            </FormControl>

            <FormControl
              fullWidth
              required
              sx={textFieldSx}
            >
              <InputLabel htmlFor="email-address">
                Email Address
              </InputLabel>

              <OutlinedInput
                id="email-address"
                label="Email Address"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
              />
            </FormControl>

            <FormControl
              fullWidth
              required
              error={
                Boolean(form.confirmEmail) &&
                form.email.trim().toLowerCase() !==
                  form.confirmEmail.trim().toLowerCase()
              }
              sx={textFieldSx}
            >
              <InputLabel htmlFor="confirm-email-address">
                Confirm Email Address
              </InputLabel>

              <OutlinedInput
                id="confirm-email-address"
                label="Confirm Email Address"
                type="email"
                value={form.confirmEmail}
                onChange={handleChange('confirmEmail')}
              />
            </FormControl>
          </Stack>
        </Box>
      </DialogContent>

      <CommonDialogActions
        onCancel={handleClose}
        onSubmit={handleSubmit}
        submitText="Send Invite"
        disabled={!isValid}
      />
    </Dialog>
  );
};

InviteUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func
};

InviteUserModal.defaultProps = {
  onSubmit: undefined
};

export default InviteUserModal;